import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

import { CsInputMeasureComponent } from 'components/cs-input-measure/cs-input-measure';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import {
  CalorieChangeDirection,
  calorieChangeFromCalories,
  caloriesFromChange,
  isBelowMinimumIntake,
  isPositiveNumber,
} from './calorie-change';

/** who is typing the target: decides how the helper line names the reported intake */
export enum CalorieChangeAudience {
  /** a person setting their own target: "the 2,178 kcal you reported" */
  Self = 'Self',
  /** a coach setting a client's target: "the reported 2,178 kcal" */
  Coach = 'Coach',
}

/** the percent field cannot hold more than this; a typed kcal further from the base leaves it empty */
const MAX_PERCENT_SHOWN = 100;

/**
 * A daily calorie target typed either as kcal or as a change in percent (#5456, phase 2).
 *
 * It draws a Decrease / Increase switch, a percent field and a kcal field. The percent is a
 * change relative to `baseCalories`, the intake the person reported at their check-up. Typing in
 * one field fills the other; the field typed in last keeps exactly what was typed.
 *
 * Example with a base of 2,178: typing 2.7 with Decrease gives 2,119 kcal; typing 2,120 kcal
 * shows 2.7 and Decrease.
 *
 * The component holds no rule of its own about what may be applied. It hands `calories` to its
 * host, `undefined` while a field is empty, and the host decides when Apply is enabled. A target
 * below `minimumDailyIntake` shows a warning and is still handed over as typed.
 *
 * The arithmetic is in `calorie-change.ts`, where a unit test can reach it.
 * See design-docs/coaching-dashboard.md, "The % change".
 */
@Component({
  selector: 'cs-calorie-change-input',
  templateUrl: 'cs-calorie-change-input.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'calorie-change-input' },
})
export class CsCalorieChangeInputComponent implements OnChanges {
  protected LL = LL;
  protected CalorieChangeDirection = CalorieChangeDirection;
  /** the sign between the two fields; a symbol, not a word to translate */
  protected readonly equalsSign = untranslatedString('=');

  /** the intake the person reported, in kcal per day; every percent is a change relative to it */
  @Input() baseCalories: number | undefined;
  /** the lowest daily target the app would set for this person, when the check-up stored one */
  @Input() minimumDailyIntake: number | undefined;
  /** where the switch starts when the opening number equals the base, so no direction can be derived */
  @Input() defaultDirection: CalorieChangeDirection = CalorieChangeDirection.Decrease;
  @Input() audience: CalorieChangeAudience = CalorieChangeAudience.Self;

  /** the target in kcal per day; undefined while a field is empty */
  @Input() calories: number | undefined;
  @Output() caloriesChange = new EventEmitter<number | undefined>();

  @ViewChild('percentInput', { static: false }) private percentInput?: CsInputMeasureComponent;
  @ViewChild('kcalInput', { static: false }) private kcalInput?: CsInputMeasureComponent;

  protected direction: CalorieChangeDirection = CalorieChangeDirection.Decrease;
  /** what the percent field shows; undefined is an empty field */
  protected percent: number | undefined;
  /** what the kcal field shows; undefined is an empty field */
  protected kcal: number | undefined;

  /** the last value this component emitted, to tell the host's echo of it from a value the host set */
  private lastEmitted: number | undefined;
  private hasEmitted = false;

  constructor(private cd: ChangeDetectorRef) {}

  /**
   * Show a target the host set. The host also hands back every value this component emits (the
   * two-way binding); that echo must not be treated as a new value, or it would rewrite the field
   * the person is typing in.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['defaultDirection'] && changes['defaultDirection'].firstChange) {
      this.direction = this.defaultDirection;
    }
    // if the host only handed back the value this component just emitted, the fields already show it
    const isOwnEcho = !!changes['calories'] && !changes['baseCalories'] && this.hasEmitted && this.calories === this.lastEmitted;
    if ((changes['calories'] || changes['baseCalories']) && !isOwnEcho) {
      this.showCalories(this.calories);
    }
  }

  /** true when there is a reported intake to take a percent of; without one only the kcal field is drawn */
  protected get hasBase(): boolean {
    return isPositiveNumber(this.baseCalories);
  }

  /** put the cursor in the percent field, or in the kcal field when there is no percent field */
  async focus(): Promise<void> {
    await (this.percentInput ?? this.kcalInput)?.focus();
  }

  /** the person picked Decrease or Increase: the percent stays, the kcal follows */
  protected onDirectionPicked(direction: CalorieChangeDirection) {
    if (direction !== CalorieChangeDirection.Decrease && direction !== CalorieChangeDirection.Increase) return;
    if (direction === this.direction) return;
    this.direction = direction;
    this.kcal = caloriesFromChange(this.baseCalories, this.direction, this.percent);
    this.emit();
  }

  /** the person typed in the percent field: it keeps what they typed, the kcal follows */
  protected onPercentEdited(percent: number | undefined) {
    // the field also reports when it tidies its own text; a value that did not change is not an edit
    if (percent === this.percent) return;
    this.percent = percent;
    this.kcal = caloriesFromChange(this.baseCalories, this.direction, percent);
    this.emit();
  }

  /** the person typed in the kcal field: it keeps what they typed, the percent and the switch follow */
  protected onKcalEdited(kcal: number | undefined) {
    if (kcal === this.kcal) return;
    this.kcal = kcal;
    this.derivePercentFromKcal();
    this.emit();
  }

  /** show a target the host set: fill the kcal field and derive the percent and the switch from it */
  private showCalories(calories: number | undefined) {
    this.kcal = typeof calories === 'number' && Number.isFinite(calories) ? calories : undefined;
    this.derivePercentFromKcal();
    this.cd.markForCheck();
  }

  /** work the percent and the switch out from the kcal field; an empty kcal field empties the percent */
  private derivePercentFromKcal() {
    const change = calorieChangeFromCalories(this.baseCalories, this.kcal);
    // if the change has no direction (the target equals the base), the switch stays where it is
    if (change?.direction) {
      this.direction = change.direction;
    }
    // a target more than twice the base has no percent the field can hold; it stays empty and
    // the helper line names the real percent
    this.percent = change && change.percent <= MAX_PERCENT_SHOWN ? change.percent : undefined;
  }

  /** hand the kcal field's value to the host, and remember it so the host's echo of it can be told apart */
  private emit() {
    this.lastEmitted = this.kcal;
    this.hasEmitted = true;
    this.caloriesChange.emit(this.kcal);
    this.cd.markForCheck();
  }

  /** "2.7% less than the 2,178 kcal you reported is 2,120 kcal."; nothing while there is no target or no base */
  protected get helperText(): TranslatedString | undefined {
    const change = calorieChangeFromCalories(this.baseCalories, this.kcal);
    if (!change || this.kcal === undefined || this.baseCalories === undefined) return undefined;
    const params = { percent: change.percent, base: Math.round(this.baseCalories), kcal: Math.round(this.kcal) };
    const texts = this.audience === CalorieChangeAudience.Coach ? LL.calorieChange.coach : LL.calorieChange.self;
    switch (change.direction) {
      case CalorieChangeDirection.Decrease: return texts.less(params);
      case CalorieChangeDirection.Increase: return texts.more(params);
      default: return texts.same({ base: params.base });
    }
  }

  /** true when the typed target is below the lowest target the app would set */
  protected get isBelowMinimum(): boolean {
    return isBelowMinimumIntake(this.kcal, this.minimumDailyIntake);
  }

  /** the warning for a target below that minimum; it names both numbers */
  protected get belowMinimumText(): TranslatedString | undefined {
    if (!this.isBelowMinimum || this.kcal === undefined || this.minimumDailyIntake === undefined) return undefined;
    return LL.calorieChange.belowMinimum({ kcal: Math.round(this.kcal), minimum: Math.round(this.minimumDailyIntake) });
  }
}
